import cn from 'classnames'
import Image from 'next/image'
import {FC} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonSize, ButtonVariant} from '@/components/common/content/ButtonTypes'
import {useCommonContent} from '@/content/common'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

import {Text, TextFontWeight, TextVariant} from '../content/Text'
import {LocalizedNumberFormat} from '../LocalizedNumberFormat'

type TileProps = {
  title: string
  titleVariant?: TextVariant
  description?: string
  imageSrc?: string
  imageAlt?: string
  price?: number
  buttonText?: string
  buttonVariant?: ButtonVariant
  buttonSize?: ButtonSize
  isCentered?: boolean
  priceNote?: string
  isFinalPrice?: boolean
}

export const Tile: FC<React.PropsWithChildren<TileProps>> = ({
  title,
  titleVariant = TextVariant.h4,
  description,
  imageSrc,
  imageAlt,
  price,
  buttonText,
  buttonVariant = ButtonVariant.secondary,
  buttonSize = ButtonSize.small,
  isCentered = true,
  priceNote,
  isFinalPrice = true,
}) => {
  const {t} = useTranslation()
  const {fromTitle} = useCommonContent()

  return (
    <>
      <li className={cn('tile', isCentered && 'centered')}>
        {imageSrc && imageAlt && (
          <div className="image">
            <Image
              src={imageSrc}
              alt={imageAlt}
              style={{objectFit: 'contain'}}
              width={80}
              height={64}
            />
          </div>
        )}
        <Text variant={titleVariant} element="h3">
          {title}
        </Text>
        {price && (
          <div className="price">
            <Text
              variant={TextVariant.h5}
              element="p"
              color={theme.palette.text.primary}
              fontWeight={TextFontWeight.bold}
            >
              {!isFinalPrice && `${t(fromTitle)} `}
              <LocalizedNumberFormat value={price} type="currency" />
            </Text>
            {priceNote && (
              <Text color={theme.palette.text.light}>
                <span className="price-note">{priceNote}</span>
              </Text>
            )}
          </div>
        )}
        {description && (
          <div className="description">
            <Text color={theme.palette.text.light}>{description}</Text>
          </div>
        )}

        {buttonText && (
          <div className="button">
            <Button variant={buttonVariant} size={buttonSize} type="button">
              {buttonText}
            </Button>
          </div>
        )}
      </li>

      <style jsx>{`
        .tile {
          flex: 1 0 330px;
          max-width: 330px;
          padding: 0 ${theme.spacing[4]};
          margin: ${theme.spacing[8]} ${theme.spacing[3]} 0;
        }

        .image {
          position: relative;
          width: 80px;
          height: 64px;
          margin-bottom: ${theme.spacing[3]};
        }

        .description {
          margin-top: ${theme.spacing[2]};
        }

        .price {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          margin-top: ${theme.spacing[2]};
        }

        .price-note {
          display: block;
          margin-top: 2px;
        }

        .price-note::before {
          content: '/';
          position: relative;
          margin-left: ${theme.spacing[2]};
          margin-right: ${theme.spacing[1]};
        }

        .button {
          margin-top: ${theme.spacing[3]};
        }

        .centered {
          text-align: center;
        }

        .centered .image {
          margin-left: auto;
          margin-right: auto;
        }

        .centered .price {
          justify-content: center;
        }

        @media ${TABLET_SCREENSIZE} {
          .tile {
            margin-top: ${theme.spacing[5]};
          }
        }

        @media ${MOBILE_SCREENSIZE} {
          .tile {
            max-width: 100%;
            margin-left: 0;
            margin-right: 0;
            margin-top: ${theme.spacing[6]};
            padding: 0;
          }

          .tile + .tile {
            margin-top: ${theme.spacing[5]};
          }

          .price {
            margin-top: ${theme.spacing[1]};
          }

          .description {
            margin-top: ${theme.spacing[1]};
          }
        }
      `}</style>
    </>
  )
}
